import { useCallback, useEffect, useState } from 'react';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from "react-router/dom";
import { useSetAtom } from 'jotai';
import { useRegisterSW } from 'virtual:pwa-register/react'
import { pwaInfo } from 'virtual:pwa-info'
import IntroPage from './pages/IntroPage';
import SearchPage from './pages/SearchPage';
import MediaHubPage from './pages/MediaHubPage';
import CollectionPage from './pages/CollectionPage';
import HistoryPage from './pages/HistoryPage';
import { PageId } from './types';
import { userAtom } from './atoms/userAtom';
import { requestAuthCheck, requestAuthTelegram } from './utils/api';
import MainLayout from './layouts/MainLayout';

console.log('pwaInfo: ', pwaInfo);
// replaced dynamically
const buildDate: string = '__DATE__';
console.log('Build date: ', buildDate);
// replaced dynamically
const reloadSW: string = '__RELOAD_SW__';

const App = () => {
  const [tagScriptLoaded, setTagScriptLoaded] = useState(false);
  const setUser = useSetAtom(userAtom); // Jotai atom setter

  const setCSSViewportVariables = useCallback(() => {
    const viewport = window.visualViewport ? {
      width: window.visualViewport.width || 0,
      height: window.visualViewport.height || 0,
      xDiff: (window.innerWidth - window.visualViewport.width) || 0,
      yDiff: (window.innerHeight - window.visualViewport.height) || 0,
    } : { // fallback if "window.visualViewport" unsupported
      width: window.innerWidth || 0,
      height: window.innerHeight || 0,
      xDiff: 0,
      yDiff: 0
    };

    document.documentElement.style.setProperty('--visual-viewport-height', `${viewport.height}px`);
    document.documentElement.style.setProperty('--visual-viewport-width', `${viewport.width}px`);
    document.documentElement.style.setProperty('--visual-viewport-x-diff', `${viewport.xDiff}px`);
    document.documentElement.style.setProperty('--visual-viewport-y-diff', `${viewport.yDiff}px`);
  }, []);

  useEffect(() => {
    const hashParams = new URLSearchParams(decodeURIComponent(window.location.hash.substring(1)));
    // Check if Telegram Mini App
    if (hashParams.has('tgWebAppData') && hashParams.has('auth_date') && hashParams.has('hash')) {
      const tgScriptEl = document.createElement('script');
      tgScriptEl.addEventListener('load', () => {
        window.Telegram.WebApp.ready();
        setTagScriptLoaded(true);
      }, { once: true });
      tgScriptEl.src = 'https://telegram.org/js/telegram-web-app.js';
      document.head.appendChild(tgScriptEl);
    } else { // Regular web app: Check user authentication
      requestAuthCheck().then(user => setUser(user));
    }

    if (typeof window.visualViewport?.addEventListener === 'function') {
      window.visualViewport.addEventListener('resize', setCSSViewportVariables);
    } else { // fallback if "window.visualViewport" unsupported
      window.addEventListener('resize', setCSSViewportVariables);
    }
    setCSSViewportVariables();

    return () => {
      if (typeof window.visualViewport?.addEventListener === 'function') {
        window.visualViewport.removeEventListener('resize', setCSSViewportVariables);
      } else { // fallback if "window.visualViewport" unsupported
        window.removeEventListener('resize', setCSSViewportVariables);
      }
    };
  }, []);

  useEffect(() => {
    if (!tagScriptLoaded || !window.Telegram?.WebApp?.initData) {
      return;
    }

    const { WebApp } = window.Telegram;

    // Authenticate Telegram user
    requestAuthTelegram(WebApp.initData).then((user) => setUser(user));
    //window.Telegram.WebApp.showAlert(Telegram.WebApp.platform);
    // Telegram Web App-specific adjustments
    try {
      if (['mobile', 'ios', 'android'].includes(WebApp.platform)) {
        WebApp.expand();
        WebApp.disableVerticalSwipes();
        WebApp.requestFullscreen();
        WebApp.setHeaderColor('#ffffff');
      }
    } catch (err) {
      console.error(err);
    }
  }, [tagScriptLoaded]);

  useRegisterSW({
    onRegisteredSW(swUrl, swReg) {
      console.log('Service Worker at: ', swUrl);
      if (reloadSW === 'true') {
        if (swReg) {
          setInterval(() => {
            console.log('Checking for sw update...')
            swReg.update();
          }, 60000 /* 60s for testing purposes */);
        }
      } else {
        console.log('SW Registered: ', swReg);
      }
    },
    onRegisterError(error: string) {
      console.error('SW registration error: ', error);
    },
  });

  const router = createBrowserRouter([
    {
      Component: MainLayout,
      children: [
        {
          index: true,
          Component: IntroPage,
          handle: { pageId: PageId.INTRO }
        },
        {
          path: `${PageId.HISTORY}`,
          element: <HistoryPage />,
          handle: { pageId: PageId.HISTORY, auth: true }
        },
        {
          path: `${PageId.SERIES}`,
          element: <MediaHubPage pageId={PageId.SERIES} />,
          handle: { pageId: PageId.SERIES, auth: true }
        },
        {
          path: `${PageId.SERIES}/:showKey`,
          element: <SearchPage />,
          handle: { pageId: PageId.SERIES, auth: true }
        },
        {
          path: `${PageId.MOVIES}`,
          element: <MediaHubPage pageId={PageId.MOVIES} />,
          handle: { pageId: PageId.MOVIES, auth: true }
        },
        {
          path: `${PageId.MOVIES}/:showKey`,
          element: <SearchPage />,
          handle: { pageId: PageId.MOVIES, auth: true }
        },
        {
          path: `${PageId.YOUTUBE}`,
          element: <SearchPage />,
          handle: { pageId: PageId.YOUTUBE, auth: true }
        },
        {
          path: `${PageId.YOUTUBE}/:showKey`,
          element: <SearchPage />,
          handle: { pageId: PageId.YOUTUBE, auth: true }
        },
        {
          path: `${PageId.COLLECTION}/*`,
          element: <CollectionPage />,
          handle: { pageId: PageId.COLLECTION, auth: true }
        },
      ],
    },
  ]);

  return (
    <RouterProvider router={router} />
  );
};

export default App;
